import { CHANGE_RISK_LABELS, type ChangeCalendarItem } from '@gb-ticket/shared-types';
import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const DAY_VISIBLE = 3;

export function ChangeCalendarDayCell({
  day,
  items,
  isToday,
  formatRange,
}: {
  day: number;
  items: ChangeCalendarItem[];
  isToday: boolean;
  formatRange: (item: ChangeCalendarItem) => string;
}) {
  const visible = items.slice(0, DAY_VISIBLE);
  const overflow = items.slice(DAY_VISIBLE);

  return (
    <div
      id={`tag-${day}`}
      className={cn(
        'min-h-28 bg-background p-2',
        isToday && 'bg-brand-navy-soft/40 ring-1 ring-brand-navy/40 ring-inset',
      )}
    >
      <p
        className={cn(
          'mb-1 text-xs',
          isToday ? 'font-semibold text-brand-navy' : 'text-muted-foreground',
        )}
      >
        {day}
        {isToday ? ' · Heute' : ''}
      </p>
      <ul className="space-y-1">
        {visible.map((item) => (
          <li key={item.id}>
            <ChangeDayLink item={item} formatRange={formatRange} />
          </li>
        ))}
      </ul>
      {overflow.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 h-auto px-1.5 py-0.5 text-xs text-muted-foreground"
            >
              +{overflow.length} weitere
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-w-72">
            {items.map((item) => (
              <DropdownMenuItem key={item.id} asChild>
                <Link to={`/helpdesk/tickets/${item.number}`} className="flex items-start gap-2">
                  {item.approved ? (
                    <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-emerald-700" aria-hidden />
                  ) : (
                    <span
                      className="mt-1 size-1.5 shrink-0 rounded-full bg-amber-500"
                      aria-hidden
                    />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      #{item.number} {item.subject}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {CHANGE_RISK_LABELS[item.risk]} ·{' '}
                      {item.approved ? 'Freigegeben' : 'Freigabe offen'} · {formatRange(item)}
                    </span>
                  </span>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

function ChangeDayLink({
  item,
  formatRange,
}: {
  item: ChangeCalendarItem;
  formatRange: (item: ChangeCalendarItem) => string;
}) {
  const approvalLabel = item.approved ? 'Freigegeben' : 'Freigabe offen';
  return (
    <Link
      to={`/helpdesk/tickets/${item.number}`}
      className={cn(
        'flex items-start gap-1 rounded px-1.5 py-1 text-xs leading-snug hover:underline',
        item.risk === 'HIGH' && 'bg-red-50 text-red-900',
        item.risk === 'MEDIUM' && 'bg-amber-50 text-amber-950',
        item.risk === 'LOW' && 'bg-emerald-50 text-emerald-900',
      )}
      title={`${item.subject} · ${CHANGE_RISK_LABELS[item.risk]} · ${approvalLabel} · ${formatRange(item)}`}
    >
      {item.approved ? (
        <CheckIcon className="mt-0.5 size-3 shrink-0 text-emerald-700" aria-label="Freigegeben" />
      ) : (
        <span
          className="mt-1.5 size-1.5 shrink-0 rounded-full bg-amber-500"
          title="Freigabe offen"
          aria-label="Freigabe offen"
        />
      )}
      <span className="min-w-0 truncate">
        #{item.number} {item.subject}
      </span>
    </Link>
  );
}
